import { Link } from "react-router-dom";
import { Briefcase, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Briefcase className="w-6 h-6 text-primary" />
              <span className="font-semibold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Hireun
              </span>
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Terms of Service
        </h1>
        <p className="text-muted-foreground mb-8">Last updated: January 16, 2025</p>

        <div className="prose prose-gray max-w-none space-y-6">
          <section>
            <h2 className="text-xl font-semibold mb-3">1. Agreement to Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              By accessing or using Hireun ("Service"), you agree to be bound by these Terms of Service ("Terms"). 
              If you disagree with any part of these terms, you do not have permission to access the Service. 
              These Terms apply to all visitors, users, and others who access or use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">2. Description of Service</h2>
            <p className="text-muted-foreground leading-relaxed">
              Hireun is an AI-powered interview preparation platform that helps users prepare for job interviews 
              by generating personalized content based on their CV and target job descriptions. The Service includes 
              interview scripts, common questions and answers, and AI-assisted refinement features.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">3. User Accounts</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">When you create an account with us, you must:</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Provide accurate, complete, and current information</li>
              <li>Maintain the security of your password and account</li>
              <li>Accept responsibility for all activities that occur under your account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              We reserve the right to refuse service, terminate accounts, or remove content at our sole discretion.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">4. Free and Premium Services</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              <strong>Free Tier:</strong> Users receive 5 free interview preparations. After reaching this limit, 
              a premium subscription is required for continued use.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              <strong>Premium Subscription:</strong> Available at €3.99 per month, providing unlimited interview 
              preparations, unlimited chat refinements, ad-free experience, and priority generation.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">5. Payments and Billing</h2>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Premium subscriptions are billed monthly through Stripe</li>
              <li>You authorize us to charge your payment method on a recurring basis</li>
              <li>Prices are subject to change with reasonable notice</li>
              <li>You may cancel your subscription at any time through your account settings</li>
              <li>Refunds are provided in accordance with our refund policy</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">6. Acceptable Use</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Use the Service for any illegal purpose or in violation of any laws</li>
              <li>Upload false, misleading, or fraudulent information</li>
              <li>Attempt to gain unauthorized access to the Service or its related systems</li>
              <li>Interfere with or disrupt the integrity or performance of the Service</li>
              <li>Use the Service to generate content that is harmful, abusive, or discriminatory</li>
              <li>Resell, redistribute, or commercially exploit the Service without permission</li>
              <li>Use automated systems or software to extract data from the Service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">7. Intellectual Property</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Service and its original content, features, and functionality are owned by Hireun and are 
              protected by international copyright, trademark, and other intellectual property laws. 
              Content generated by our AI for your personal use belongs to you, but you grant us a license 
              to use anonymized data to improve our services.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">8. User Content</h2>
            <p className="text-muted-foreground leading-relaxed">
              You retain ownership of content you upload (such as your CV). By uploading content, you grant us 
              a limited license to process and use it solely for providing the Service. You are responsible 
              for ensuring you have the right to share any content you upload.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">9. AI-Generated Content Disclaimer</h2>
            <p className="text-muted-foreground leading-relaxed">
              Content generated by our AI is provided for informational purposes and interview preparation guidance only. 
              We do not guarantee the accuracy, completeness, or suitability of any AI-generated content. 
              You are responsible for reviewing and adapting the content to your specific situation. 
              AI-generated content should not be considered as professional career advice.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">10. Third-Party Services</h2>
            <p className="text-muted-foreground leading-relaxed">
              Our Service may contain links to third-party websites or services that are not owned or controlled by Hireun. 
              We have no control over, and assume no responsibility for, the content, privacy policies, or practices 
              of any third-party websites or services.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">11. Disclaimer of Warranties</h2>
            <p className="text-muted-foreground leading-relaxed">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. 
              WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE. WE DO NOT GUARANTEE 
              ANY SPECIFIC RESULTS FROM USE OF THE SERVICE, INCLUDING JOB INTERVIEW SUCCESS.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">12. Limitation of Liability</h2>
            <p className="text-muted-foreground leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, HIREUN SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
              SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR OTHER INTANGIBLE LOSSES, 
              RESULTING FROM YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE SERVICE.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">13. Indemnification</h2>
            <p className="text-muted-foreground leading-relaxed">
              You agree to defend, indemnify, and hold harmless Hireun and its officers, directors, employees, 
              and agents from any claims, damages, obligations, losses, liabilities, costs, or debt arising from 
              your use of the Service or violation of these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">14. Termination</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may terminate or suspend your account immediately, without prior notice, for any reason, including 
              breach of these Terms. Upon termination, your right to use the Service will immediately cease. 
              You may delete your account at any time through your account settings.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">15. Changes to Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              We reserve the right to modify these Terms at any time. We will provide notice of significant changes 
              by posting the new Terms on this page and updating the "Last updated" date. Your continued use of the 
              Service after such modifications constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">16. Governing Law</h2>
            <p className="text-muted-foreground leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the European Union, 
              without regard to its conflict of law provisions. Any disputes arising from these Terms will be 
              resolved in the competent courts.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">17. Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have any questions about these Terms, please contact us at:
            </p>
            <ul className="list-none mt-3 space-y-1 text-muted-foreground">
              <li>Email: legal@hireun.com</li>
              <li>Website: hireun.com</li>
            </ul>
          </section>
        </div>

        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t flex gap-4 text-sm">
          <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
          <Link to="/" className="text-primary hover:underline">Home</Link>
        </div>
      </main>
    </div>
  );
};

export default TermsOfServicePage;
